import React from 'react'

import classNames from 'classnames'
import styled from 'styled-components'
import {Container, media} from 'styled-bootstrap-grid'
import HeartFillIcon from 'remixicon-react/HeartFillIcon'
import HeartLineIcon from 'remixicon-react/HeartLineIcon'

import {ProductDetail, User, Variant} from '@festi/common/api/rest'
import {MainIconButton} from '@festi/common/components/buttons'
import {AddToCartButton} from '@festi/common/components/cart'
import {H6, OldPriceText, PriceText} from '@festi/common/components/typography'
import {useAuth, useComparison} from '@festi/common/contexts'
import {styledTheme} from '@festi/common/themes'
import {useWishlist} from '@festi/common/utils/wishlists'
import {formatPrice} from '@festi/utils/numbers'

const Root = styled.div`
  position: fixed;
  bottom: 0;
  right: 0;
  left: 0;
  height: 72px;
  padding-block: 12px;
  background-color: ${styledTheme.palette.white};
  border-radius: 8px 8px 0 0;
  box-shadow: ${styledTheme.boxShadow.shadowToTop};
  z-index: 460;
  opacity: 1;
  transition: opacity 200ms ease-out;

  &.hide {
    opacity: 0;
    pointer-events: none;
  }

  &.isComparing {
    bottom: 120px;
  }

  ${media.md`
    height: 84px;
    padding-block: 8px;
    border-radius: 0;
  `}
`

const Wrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`

const LeftSectionWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;

  ${media.md`
    gap: 12px;
  `}
`

const ProductImage = styled.img`
  width: 48px;
  height: 48px;
  object-fit: contain;

  ${media.md`
    width: 68px;
    height: 68px;
  `}
`
const RightSectionWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex: 1;
  justify-content: space-between;

  ${media.md`
    flex: unset;
    justify-content: unset;
    gap: 24px;
  `}
`

const PriceWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const ActionsWrapper = styled.div`
  display: flex;
  gap: 8px;
`

const HideInMobile = styled.div`
  display: none;

  ${media.md`
    display: block;
  `}
`

interface Props {
  isInView: boolean
  user: User
  product: ProductDetail
  variant: Variant
  price: number
  lowestPrice: number
}

export default function ProductInfoScrollRow({
  isInView,
  user,
  product,
  variant,
  price,
  lowestPrice,
}: Props) {
  const {promptAuth, setPromptAuth} = useAuth()
  const {isVariantInWishlist, toggleVariantInWishlist} = useWishlist()
  const {isComparing} = useComparison()

  const hasDiscount = lowestPrice < price

  return (
    <Root className={classNames({hide: isInView, isComparing})}>
      <Container>
        <Wrapper>
          <LeftSectionWrapper>
            <ProductImage
              width={68}
              height={68}
              alt={variant.images[0].alt}
              src={variant.images[0].image.productSmall}
            />
            <HideInMobile>
              <H6>{product.name}</H6>
            </HideInMobile>
          </LeftSectionWrapper>
          <RightSectionWrapper>
            <PriceWrapper>
              {!!hasDiscount && (
                <OldPriceText oldPriceSize={'14px'}>
                  {formatPrice(price)}
                </OldPriceText>
              )}
              <PriceText size={'24px'}>{formatPrice(lowestPrice)}</PriceText>
            </PriceWrapper>
            <ActionsWrapper>
              <AddToCartButton
                variant={variant}
                product={product}
                newDesign
                ariaHidden={isInView}
                tabIndex={isInView ? -1 : 0}
              />
              <HideInMobile>
                <MainIconButton
                  onClick={() =>
                    !user
                      ? setPromptAuth(!promptAuth)
                      : toggleVariantInWishlist(variant)
                  }
                  icon={
                    isVariantInWishlist(variant.sku)
                      ? HeartFillIcon
                      : HeartLineIcon
                  }
                  iconColor={
                    isVariantInWishlist(variant.sku)
                      ? styledTheme.palette.lightBlue
                      : styledTheme.palette.ui20Solid
                  }
                  buttonVariant="ghost"
                  tabIndex={isInView ? -1 : 0}
                  aria-hidden={isInView}
                />
              </HideInMobile>
            </ActionsWrapper>
          </RightSectionWrapper>
        </Wrapper>
      </Container>
    </Root>
  )
}
